import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Bot, User as UserIcon, Send, Phone,
  MoreVertical, MessageSquare, CheckCheck, Check,
  Star, Archive, Trash2, Paperclip, Smile,
  RefreshCw, X, Zap, Filter,
} from 'lucide-react';
import { Input }      from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api       from '@/lib/api';
import { toast } from 'sonner';
import { format, isToday, isYesterday } from 'date-fns';

// ─── constants ────────────────────────────────────────────────────────────────
const STATUS_MAP = {
  active:   { dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Active'   },
  pending:  { dot: 'bg-amber-400',   pill: 'bg-amber-50  text-amber-700  border-amber-200',   label: 'Pending'  },
  resolved: { dot: 'bg-blue-400',    pill: 'bg-blue-50   text-blue-700   border-blue-200',    label: 'Resolved' },
  archived: { dot: 'bg-gray-400',    pill: 'bg-gray-100  text-gray-500   border-gray-200',    label: 'Archived' },
};

const PRIORITY_COLOR = {
  urgent: 'text-rose-500',
  high:   'text-orange-400',
  medium: 'text-amber-400',
  low:    'text-gray-300',
};

const PRIORITY_OPTIONS = [
  { v: 'urgent', color: 'text-rose-500',   fill: true,  label: 'Urgent'  },
  { v: 'high',   color: 'text-orange-400', fill: true,  label: 'High'    },
  { v: 'medium', color: 'text-amber-400',  fill: false, label: 'Medium'  },
  { v: 'low',    color: 'text-gray-300',   fill: false, label: 'Low'     },
];

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtListTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isToday(d))     return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'dd/MM/yy');
};
const fmtMsgTime = (ts) => format(new Date(ts), 'HH:mm');

// ✅ FIX: Get message text from either schema format
// messageHandler saves as `message`, API adds messages save as `message_text`
const getMsgText = (msg) =>
  msg.message || msg.message_text || msg.content || '';

// ✅ FIX: Determine if message is from customer
const isCustomerMsg = (msg) =>
  msg.direction === 'incoming' ||
  msg.direction === 'inbound'  ||
  msg.sender_type === 'customer';

// ✅ FIX: Determine if message is from bot
const isBotMsg = (msg) =>
  msg.is_ai_response === true ||
  msg.sender_type === 'bot';

// ─── component ────────────────────────────────────────────────────────────────
export default function Conversations() {
  const [conversations,  setConversations]  = useState([]);
  const [selectedConv,   setSelectedConv]   = useState(null);
  const [messages,       setMessages]       = useState([]);
  const [newMessage,     setNewMessage]     = useState('');
  const [searchQuery,    setSearchQuery]    = useState('');
  const [loading,        setLoading]        = useState(true);
  const [sending,        setSending]        = useState(false);
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [stats,          setStats]          = useState({ total:0, active:0, pending:0, resolved:0, unread:0 });
  const [isTyping,       setIsTyping]       = useState(false);
  const [showFilters,    setShowFilters]    = useState(false);

  const endRef   = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() =>
    endRef.current?.scrollIntoView({ behavior: 'smooth' }), []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // ── fetchers ─────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/conversations/stats');
      const s = data.stats || {};
      // ✅ FIX: stats endpoint se jo bhi fields aayein support karo
      setStats({
        total:    s.total_conversations || s.total    || 0,
        active:   s.active              || 0,
        pending:  s.pending             || 0,
        resolved: s.resolved            || 0,
        unread:   s.unread              || s.total_unread_messages || 0,
      });
    } catch {}
  }, []);

  const fetchConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const p = new URLSearchParams();
      if (statusFilter   !== 'all') p.append('status',   statusFilter);
      if (priorityFilter !== 'all') p.append('priority', priorityFilter);
      p.append('sort_by',    'last_message_at');
      p.append('sort_order', 'desc');
      const { data } = await api.get(`/conversations?${p.toString()}`);
      const list = data.conversations || [];
      setConversations(list);
      // ✅ FIX: Auto-select first only if nothing selected yet
      if (!selectedConv && list.length > 0) setSelectedConv(list[0]);
    } catch {
      if (!silent) toast.error('Failed to load conversations');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [statusFilter, priorityFilter, selectedConv]);

  // ✅ FIX: Fetch messages — handle both `messages` table format
  const fetchMessages = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/conversations/${id}`);
      const msgs = data.conversation?.messages || [];
      setMessages(msgs);
    } catch {
      toast.error('Failed to load messages');
    }
  }, []);

  const markAsRead = useCallback(async (id) => {
    try {
      await api.patch(`/conversations/${id}/mark-read`);
      fetchStats();
      setConversations(prev =>
        prev.map(c => c.id === id ? { ...c, unread_count: 0 } : c)
      );
    } catch {}
  }, [fetchStats]);

  useEffect(() => {
    fetchConversations();
    fetchStats();
    const iv = setInterval(() => {
      fetchConversations(true);
      fetchStats();
    }, 8000); // ✅ FIX: 8s poll for near-realtime updates
    return () => clearInterval(iv);
  }, [fetchConversations, fetchStats]);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv.id);
      markAsRead(selectedConv.id);
    }
  }, [selectedConv, fetchMessages, markAsRead]);

  // ── actions ──────────────────────────────────────────────────────────────────
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConv || sending) return;

    const tempId = `temp-${Date.now()}`;
    const temp = {
      id:              tempId,
      conversation_id: selectedConv.id,
      message:         newMessage,       // ✅ FIX: use 'message' not 'content'
      message_text:    newMessage,
      content:         newMessage,
      sender_type:     'agent',
      direction:       'outgoing',
      is_ai_response:  false,
      created_at:      new Date().toISOString(),
    };
    const txt = newMessage;
    setMessages(p => [...p, temp]);
    setNewMessage('');
    setSending(true);

    try {
      await api.post(`/conversations/${selectedConv.id}/messages`, {
        messageText: txt,
        messageType: 'text',
        senderType:  'agent',
      });
      // ✅ FIX: Refresh messages after send to get real DB record
      await fetchMessages(selectedConv.id);
      fetchConversations(true);
    } catch {
      setMessages(p => p.filter(m => m.id !== tempId));
      setNewMessage(txt);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const updateStatus = async (status) => {
    if (!selectedConv) return;
    try {
      await api.patch(`/conversations/${selectedConv.id}/status`, { status });
      setSelectedConv(prev => ({ ...prev, status }));
      setConversations(prev =>
        prev.map(c => c.id === selectedConv.id ? { ...c, status } : c)
      );
      toast.success(`Marked as ${status}`);
    } catch { toast.error('Failed to update status'); }
  };

  const updatePriority = async (priority) => {
    if (!selectedConv) return;
    try {
      await api.patch(`/conversations/${selectedConv.id}/priority`, { priority });
      setSelectedConv(prev => ({ ...prev, priority }));
      setConversations(prev =>
        prev.map(c => c.id === selectedConv.id ? { ...c, priority } : c)
      );
      toast.success(`Priority → ${priority}`);
    } catch { toast.error('Failed to update priority'); }
  };

  const takeover = async () => {
    if (!selectedConv) return;
    try {
      await api.patch(`/conversations/${selectedConv.id}/status`, { status: 'active' });
      toast.success('You are now handling this conversation');
      fetchConversations();
    } catch { toast.error('Failed to take over'); }
  };

  const deleteConv = async () => {
    if (!selectedConv || !window.confirm('Delete this conversation?')) return;
    try {
      await api.delete(`/conversations/${selectedConv.id}`);
      toast.success('Conversation deleted');
      setSelectedConv(null);
      setMessages([]);
      fetchConversations();
    } catch { toast.error('Failed to delete conversation'); }
  };

  // ── filtered ─────────────────────────────────────────────────────────────────
  const filtered = conversations.filter(c =>
    (c.customer_name?.toLowerCase()  || '').includes(searchQuery.toLowerCase()) ||
    (c.customer_phone                || '').includes(searchQuery) ||
    (c.customer_email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  // ── loading ──────────────────────────────────────────────────────────────────
  if (loading && !conversations.length) return (
    <div className="h-[calc(100vh-140px)] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
          <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
        </div>
        <p className="text-sm text-gray-400 font-medium">Loading conversations…</p>
      </div>
    </div>
  );

  // ─── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="h-[calc(100vh-140px)] flex -m-6 overflow-hidden" data-testid="conversations-page">

      {/* ════════════════════════════════════════════════════════
          LEFT SIDEBAR
      ════════════════════════════════════════════════════════ */}
      <div className="w-80 flex-shrink-0 bg-white border-r border-gray-100 flex flex-col">

        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-100 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-gray-900">Conversations</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowFilters(s => !s)}
                className={`p-1.5 rounded-lg transition-all ${
                  showFilters
                    ? 'bg-emerald-100 text-emerald-600'
                    : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => { fetchConversations(); fetchStats(); }} disabled={loading}
                className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-40"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search name, phone…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-9 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-xs"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Status pills */}
          <div className="flex gap-1 overflow-x-auto scrollbar-none">
            {['all','active','pending','resolved'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                  statusFilter === s
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {/* Priority filter */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Priority</p>
                <div className="flex flex-wrap gap-1">
                  {['all','urgent','high','medium','low'].map(p => (
                    <button
                      key={p}
                      onClick={() => setPriorityFilter(p)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                        priorityFilter === p
                          ? 'bg-violet-500 text-white'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mini stats */}
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { label: 'Total',  value: stats.total  || 0, color: 'text-gray-900'    },
              { label: 'Active', value: stats.active || 0, color: 'text-emerald-600' },
              { label: 'Unread', value: stats.unread || 0, color: 'text-rose-500'    },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-gray-50 rounded-xl py-2 text-center">
                <div className={`text-base font-bold tabular-nums ${color}`}>{value}</div>
                <div className="text-[10px] text-gray-400">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Conversation List */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            <AnimatePresence>
              {filtered.length > 0 ? filtered.map(conv => {
                const st       = STATUS_MAP[conv.status] || STATUS_MAP.active;
                const isActive = selectedConv?.id === conv.id;
                return (
                  <motion.button
                    key={conv.id}
                    onClick={() => setSelectedConv(conv)}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0   }}
                    exit={   { opacity: 0, x: -12 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full p-3 rounded-xl text-left transition-all duration-150 ${
                      isActive
                        ? 'bg-emerald-50 border border-emerald-200 shadow-sm'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-emerald-100' : 'bg-gray-100'
                        }`}>
                          <UserIcon className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} />
                        </div>
                        {conv.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                            <span className="text-[9px] font-bold text-white">{conv.unread_count}</span>
                          </div>
                        )}
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={`text-xs font-semibold truncate ${isActive ? 'text-emerald-700' : 'text-gray-900'}`}>
                            {conv.customer_name || conv.customer_phone || 'Unknown'}
                          </span>
                          <span className="text-[10px] text-gray-400 flex-shrink-0 ml-1">
                            {fmtListTime(conv.last_message_at || conv.updated_at)}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 truncate mb-1">{conv.customer_phone}</p>
                        {/* ✅ FIX: Show last message preview */}
                        <p className="text-[11px] text-gray-600 truncate">
                          {conv.last_message || 'No messages yet'}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${st.pill}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                            {st.label}
                          </span>
                          {conv.priority && conv.priority !== 'medium' && (
                            <Star className={`w-2.5 h-2.5 ${PRIORITY_COLOR[conv.priority]}`} fill="currentColor" />
                          )}
                          {conv.is_bot_active && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-100 text-[10px] text-violet-600 font-medium">
                              <Bot className="w-2.5 h-2.5" /> AI
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.button>
                );
              }) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                    <MessageSquare className="w-5 h-5 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">No conversations</p>
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="mt-2 text-xs text-emerald-600 hover:underline">
                      Clear search
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </ScrollArea>
      </div>

      {/* ════════════════════════════════════════════════════════
          RIGHT — CHAT PANEL
      ════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col overflow-hidden bg-[#f0fdf4]">
        {selectedConv ? (
          <>
            {/* Chat header */}
            <div className="px-5 py-3 bg-white border-b border-gray-100 shadow-sm flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <UserIcon className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">
                    {selectedConv.customer_name || selectedConv.customer_phone || 'Unknown'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Phone className="w-3 h-3" />
                    <span>{selectedConv.customer_phone}</span>
                    {selectedConv.is_bot_active && (
                      <span className="flex items-center gap-1 text-violet-500 font-semibold">
                        <Zap className="w-3 h-3" /> AI active
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <Select value={selectedConv.status} onValueChange={updateStatus}>
                  <SelectTrigger className="h-8 w-28 rounded-xl border-gray-200 text-xs focus:border-emerald-400">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_MAP).map(([v, { label }]) => (
                      <SelectItem key={v} value={v}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all">
                      <Star
                        className={`w-4 h-4 ${PRIORITY_COLOR[selectedConv.priority] || 'text-gray-300'}`}
                        fill={selectedConv.priority === 'urgent' || selectedConv.priority === 'high' ? 'currentColor' : 'none'}
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    {PRIORITY_OPTIONS.map(({ v, color, fill, label }) => (
                      <DropdownMenuItem key={v} onClick={() => updatePriority(v)}>
                        <Star className={`w-4 h-4 mr-2 ${color}`} fill={fill ? 'currentColor' : 'none'} />
                        {label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all">
                      <MoreVertical className="w-4 h-4 text-gray-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {selectedConv.is_bot_active && (
                      <>
                        <DropdownMenuItem onClick={takeover}>
                          <UserIcon className="w-4 h-4 mr-2 text-emerald-600" /> Take Over
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => updateStatus('archived')}>
                      <Archive className="w-4 h-4 mr-2 text-gray-500" /> Archive
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={deleteConv} className="text-rose-600">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-4 py-4">
              <div className="space-y-1 max-w-3xl mx-auto">
                <AnimatePresence>
                  {messages.map((msg, i) => {
                    // ✅ FIX: Use helper functions for direction/type detection
                    const isCust   = isCustomerMsg(msg);
                    const isBot    = isBotMsg(msg);
                    const text     = getMsgText(msg);

                    // Show date divider if >5 min gap or first message
                    const showDate = i === 0 ||
                      new Date(msg.created_at) - new Date(messages[i-1].created_at) > 300000;

                    return (
                      <div key={msg.id}>
                        {showDate && (
                          <div className="text-center my-5">
                            <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[11px] text-gray-400 shadow-sm border border-white">
                              {format(new Date(msg.created_at), 'EEEE, dd MMMM yyyy')}
                            </span>
                          </div>
                        )}
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.18 }}
                          className={`flex mb-1 ${isCust ? 'justify-start' : 'justify-end'}`}
                        >
                          <div className={`
                            max-w-[68%] rounded-2xl px-3.5 py-2.5 shadow-sm
                            ${isCust
                              ? 'bg-white text-gray-800 rounded-tl-sm'
                              : isBot
                                ? 'bg-violet-500 text-white rounded-tr-sm'
                                : 'bg-emerald-500 text-white rounded-tr-sm'}
                          `}>
                            {isBot && (
                              <div className="flex items-center gap-1 mb-1 opacity-75">
                                <Bot className="w-3 h-3" />
                                <span className="text-[9px] font-bold uppercase tracking-wider">AI Bot</span>
                              </div>
                            )}
                            {/* ✅ FIX: Use getMsgText() helper */}
                            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                              {text || '(empty message)'}
                            </p>
                            <div className={`flex items-center justify-end gap-1 mt-1 ${isCust ? 'text-gray-400' : 'text-white/70'}`}>
                              <span className="text-[10px]">{fmtMsgTime(msg.created_at)}</span>
                              {!isCust && !isBot && (
                                msg.is_read
                                  ? <CheckCheck className="w-3 h-3 text-white/80" />
                                  : <Check className="w-3 h-3 text-white/60" />
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                </AnimatePresence>

                {/* Typing indicator */}
                {isTyping && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start mb-1">
                    <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex gap-1.5">
                      {[0, 150, 300].map(d => (
                        <div key={d} className="w-2 h-2 bg-gray-300 rounded-full animate-bounce"
                          style={{ animationDelay: `${d}ms` }} />
                      ))}
                    </div>
                  </motion.div>
                )}

                <div ref={endRef} />

                {messages.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-white shadow-sm flex items-center justify-center mb-4">
                      <MessageSquare className="w-7 h-7 text-emerald-300" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">No messages yet</p>
                    <p className="text-xs text-gray-400">Start the conversation below.</p>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Input bar */}
            <div className="px-4 py-3 bg-white border-t border-gray-100 flex-shrink-0">
              <form onSubmit={sendMessage} className="flex items-center gap-2 max-w-3xl mx-auto">
                <button type="button"
                  className="p-2 rounded-xl text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all flex-shrink-0">
                  <Smile className="w-5 h-5" />
                </button>
                <button type="button"
                  className="p-2 rounded-xl text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all flex-shrink-0">
                  <Paperclip className="w-5 h-5" />
                </button>
                <Input
                  ref={inputRef}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
                  disabled={sending}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(e);
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white flex items-center justify-center shadow-md shadow-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex-shrink-0"
                >
                  {sending
                    ? <RefreshCw className="w-4 h-4 animate-spin" />
                    : <Send className="w-4 h-4" />
                  }
                </button>
              </form>
              <p className="text-center text-[10px] text-gray-300 mt-2">Enter to send · Shift+Enter for new line</p>
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex-1 flex items-center justify-center"
          >
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-3xl bg-white border border-emerald-100 shadow-lg flex items-center justify-center">
                <MessageSquare className="w-10 h-10 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">WhatsApp Business</h3>
                <p className="text-sm text-gray-400 max-w-xs mx-auto">
                  Select a conversation from the list to start chatting with your customers.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}